import { useQuery } from '@tanstack/react-query'
import { doc, getDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { Transporter, TransporterGroup } from '@otw/models'

interface TransporterWithGroupName extends Transporter {
  group_name?: string
}

export const useTransporterGroups = (groupIds: string[], transporterIds: string[] = []) => {
  // Serialize arrays for stable query key
  const groupIdsKey = [...new Set(groupIds)].sort().join(',')
  const transporterIdsKey = [...new Set(transporterIds)].sort().join(',')
  const isEnabled = groupIds.length > 0 || transporterIds.length > 0

  return useQuery({
    queryKey: ['transporter_groups', groupIdsKey, transporterIdsKey],
    queryFn: async () => {
      const groupOwnersMap: { [groupId: string]: TransporterWithGroupName } = {}
      const driversMap: { [transporterId: string]: Transporter } = {}
      
      // Fetch group owners in parallel
      if (groupIds.length > 0) {
        const uniqueGroupIds = [...new Set(groupIds)]
        
        const groupPromises = uniqueGroupIds.map(async (groupId) => {
          try {
            const groupDoc = await getDoc(doc(db, 'transporter_groups', groupId))
            if (!groupDoc.exists()) {
              return null
            }
            
            const groupData = groupDoc.data() as TransporterGroup
            
            // Fetch the group owner
            if (groupData.owner_id) {
              const ownerDoc = await getDoc(doc(db, 'transporters', groupData.owner_id))
              if (ownerDoc.exists()) {
                const ownerData = ownerDoc.data()
                return {
                  groupId,
                  owner: {
                    id: ownerDoc.id,
                    user_id: ownerData.user_id || '',
                    email: ownerData.email || '',
                    phone: ownerData.phone,
                    first_name: ownerData.first_name,
                    last_name: ownerData.last_name,
                    created_at: ownerData.created_at,
                    updated_at: ownerData.updated_at,
                    status: ownerData.status,
                    group_name: groupData.name,
                  } as TransporterWithGroupName
                }
              }
            }
            return null
          } catch (error) {
            console.error(`Error fetching group owner for group ${groupId}:`, error)
            return null
          }
        })
        
        const groupResults = await Promise.all(groupPromises)
        groupResults.forEach(result => {
          if (result?.owner) {
            groupOwnersMap[result.groupId] = result.owner
          }
        })
      }
      
      // Fetch drivers in parallel
      if (transporterIds.length > 0) {
        const uniqueTransporterIds = [...new Set(transporterIds)]
        
        const driverPromises = uniqueTransporterIds.map(async (transporterId) => {
          try {
            const transporterDoc = await getDoc(doc(db, 'transporters', transporterId))
            if (transporterDoc.exists()) {
              const transporterData = transporterDoc.data()
              return {
                transporterId,
                driver: {
                  id: transporterDoc.id,
                  user_id: transporterData.user_id || '',
                  email: transporterData.email || '',
                  phone: transporterData.phone,
                  first_name: transporterData.first_name,
                  last_name: transporterData.last_name,
                  created_at: transporterData.created_at,
                  updated_at: transporterData.updated_at,
                  status: transporterData.status,
                } as Transporter
              }
            }
            return null
          } catch (error) {
            console.error(`Error fetching transporter ${transporterId}:`, error)
            return null
          }
        })
        
        const driverResults = await Promise.all(driverPromises)
        driverResults.forEach(result => {
          if (result?.driver) {
            driversMap[result.transporterId] = result.driver
          }
        })
      }
      
      return {
        groupOwners: groupOwnersMap,
        drivers: driversMap
      }
    },
    enabled: isEnabled,
    staleTime: 30000, // 30 seconds, same as useAdminTransporter
  })
}

// Legacy hook for backward compatibility - just fetches group owners
export const useTransporterGroupOwners = (groupIds: string[]) => {
  const { data, ...rest } = useTransporterGroups(groupIds, [])
  
  return {
    data: data?.groupOwners || {},
    ...rest
  }
}

// Hook specifically for fetching drivers
export const useTransporterDrivers = (transporterIds: string[]) => {
  const { data, ...rest } = useTransporterGroups([], transporterIds)
  
  return {
    data: data?.drivers || {},
    ...rest
  }
}

export default useTransporterGroups
